/**
 * Theme state (plan revision R15).
 *
 * The class is already on <html> before this module loads -- the inline script
 * in index.html does that, so there is no flash. This only handles changing it
 * afterwards, which means the whole runtime cost of theming is one class name
 * and one localStorage write.
 *
 * Three states rather than two. "system" is the default and genuinely different
 * from picking light: this app gets used on the sofa at night and under
 * supermarket strip lighting, and the phone already knows which it is.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = 'theme';

/** Status-bar color, kept in step so the notch area does not fight the app. */
const CHROME = { light: '#f7f2ea', dark: '#14100e' } as const;

export function readTheme(): ThemeChoice {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* private browsing: fall back to following the system */
  }
  return 'system';
}

export function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  return choice === 'system' ? (prefersDark() ? 'dark' : 'light') : choice;
}

/**
 * Apply a choice. Persisting "system" as an absence rather than a value keeps
 * the inline boot script trivial -- it only has to ask whether a key exists.
 */
export function applyTheme(choice: ThemeChoice): void {
  const resolved = resolveTheme(choice);
  document.documentElement.classList.toggle('dark', resolved === 'dark');

  try {
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* the class is applied either way; only persistence is lost */
  }

  /**
   * The media-scoped <meta> tags in index.html cover the system case. Once a
   * person overrides the system they stop being right, so the live one is set
   * here -- otherwise choosing dark on a light phone leaves a bone-white status
   * bar above a near-black app.
   */
  let live = document.querySelector<HTMLMetaElement>('meta[name="theme-color"][data-live]');
  if (!live) {
    live = document.createElement('meta');
    live.name = 'theme-color';
    live.dataset.live = 'true';
    document.head.appendChild(live);
  }
  live.content = CHROME[resolved];
}

/**
 * Follow the system while the choice is "system". Without this, changing the
 * phone's appearance at sunset leaves the app on the old ground until reload.
 */
export function watchSystemTheme(getChoice: () => ThemeChoice): () => void {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (getChoice() === 'system') applyTheme('system');
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
